import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Upload, X, FileText, Info, Camera, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CASE_TYPE_META, caseApi, formatMoney } from '../lib/orderCases';

const REASONS = {
  RETURN: [
    'Non è come me lo immaginavo',
    'Ho cambiato idea',
    'Misura o dimensioni non adatte',
    'Ho ricevuto un articolo diverso',
    'Altro motivo',
  ],
  NON_CONFORMITY: [
    'Materiale diverso da quello indicato',
    'Colore diverso da quello ordinato',
    'Misure diverse da quelle indicate',
    'Articolo diverso da quello ordinato',
    'Altro',
  ],
  NON_DELIVERY: [
    'Il pacco non è mai arrivato',
    'Il tracking risulta consegnato ma non ho ricevuto nulla',
    'Il pacco risulta bloccato da giorni',
    'Il pacco è tornato al mittente',
    'Altro',
  ],
  DEFECTIVE: [
    'Il prodotto non funziona / si è rotto subito',
    'Difetto di lavorazione',
    'Parti mancanti',
    'Altro difetto',
  ],
  DAMAGED: [
    'Prodotto rotto nel pacco',
    'Prodotto graffiato o ammaccato',
    'Imballaggio danneggiato dal corriere',
    'Altro danno',
  ],
  OTHER: ['Ho bisogno di assistenza su questo ordine'],
};

// Prove richieste per tipo di caso
const EVIDENCE = {
  DAMAGED: [
    { kind: 'PRODUCT_PHOTO', label: 'Foto del prodotto', required: true },
    { kind: 'PACKAGING_PHOTO', label: "Foto dell'imballaggio interno", required: true },
    { kind: 'PARCEL_PHOTO', label: 'Foto del pacco esterno', required: true },
  ],
  DEFECTIVE: [
    { kind: 'PRODUCT_PHOTO', label: 'Foto o documenti del difetto', required: true },
  ],
  NON_CONFORMITY: [
    { kind: 'PRODUCT_PHOTO', label: 'Foto di ciò che hai ricevuto', required: false },
  ],
  RETURN: [{ kind: 'PRODUCT_PHOTO', label: 'Foto (opzionale)', required: false }],
  NON_DELIVERY: [{ kind: 'DOCUMENT', label: 'Documenti o schermate (opzionale)', required: false }],
  OTHER: [{ kind: 'DOCUMENT', label: 'Allegati (opzionale)', required: false }],
};

export default function OrderCaseDialog({ open, onOpenChange, order, caseType, eligibility, userId, onCreated }) {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [files, setFiles] = useState([]); // {url, file_name, mime_type, kind}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const meta = CASE_TYPE_META[caseType] || CASE_TYPE_META.OTHER;
  const groups = EVIDENCE[caseType] || EVIDENCE.OTHER;
  const reasons = REASONS[caseType] || REASONS.OTHER;

  const handleUpload = async (event, kind) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    if (files.length + selected.length > 8) {
      toast.error('Puoi allegare al massimo 8 file');
      return;
    }
    setUploading(true);
    try {
      for (const file of selected) {
        const uploaded = await caseApi.upload(file);
        setFiles((prev) => [...prev, { ...uploaded, kind }]);
      }
      toast.success('File caricato');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async () => {
    setFormError('');
    if (description.trim().length < 10) {
      setFormError('Descrivi il problema con almeno 10 caratteri');
      toast.error('Descrivi il problema con almeno 10 caratteri');
      return;
    }
    const requiredMissing = groups
      .filter((g) => g.required)
      .filter((g) => !files.some((f) => f.kind === g.kind));
    if (requiredMissing.length && !videoUrl.trim()) {
      const msg = `Serve almeno: ${requiredMissing.map((g) => g.label.toLowerCase()).join(', ')} (oppure il link a un video)`;
      setFormError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    try {
      const created = await caseApi.create({
        order_id: order.id,
        buyer_id: userId,
        type: caseType,
        description: description.trim(),
        reason_code: reason || null,
        external_video_url: videoUrl.trim() || null,
        attachments: files,
      });
      toast.success('Richiesta inviata: l\'artigiano è stato avvisato');
      onOpenChange(false);
      setDescription(''); setReason(''); setFiles([]); setVideoUrl('');
      if (onCreated) onCreated(created);
      navigate(`/assistenza-ordine/${created.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="order-case-dialog">
        <DialogHeader>
          <div className={`w-12 h-12 rounded-2xl ${meta.bg} ring-1 ${meta.ring} flex items-center justify-center mb-2`}>
            <meta.Icon className={`w-6 h-6 ${meta.accent}`} />
          </div>
          <DialogTitle className="text-2xl font-bold tracking-tight">{meta.label}</DialogTitle>
          <DialogDescription>
            Ordine {order?.order_number || order?.id?.slice(0, 8)} · {formatMoney(eligibility?.order_amount)}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl bg-gradient-to-br from-[#008080]/8 to-[#00CED1]/5 border border-[#008080]/15 p-4 flex gap-3">
          <Info className="w-5 h-5 text-[#008080] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Cerchiamo prima di risolvere insieme il problema: la tua segnalazione apre una conversazione
            diretta con l'artigiano, dove potete scambiare informazioni, foto e proposte.
          </p>
        </div>

        <div className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label>Motivo</Label>
            <div className="grid gap-2">
              {reasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  data-testid={`case-reason-${r.slice(0, 12)}`}
                  className={`text-left text-sm px-4 py-2.5 rounded-xl border transition-all duration-200 ${
                    reason === r
                      ? 'border-[#008080] bg-[#008080]/5 text-[#00615f] font-medium shadow-sm'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="case-description">
              {caseType === 'NON_CONFORMITY'
                ? 'Cosa non corrisponde a quanto ordinato?'
                : 'Raccontaci cosa è successo'}
            </Label>
            <Textarea
              id="case-description"
              data-testid="case-description-input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Più dettagli fornisci, più veloce sarà trovare una soluzione…"
            />
          </div>

          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.kind} className="rounded-2xl border border-dashed border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Camera className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">
                      {g.label} {g.required && <span className="text-rose-500">*</span>}
                    </span>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => handleUpload(e, g.kind)}
                      data-testid={`case-upload-${g.kind}`}
                    />
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#008080] hover:text-[#006666] transition-colors">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      Carica
                    </span>
                  </label>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {files.filter((f) => f.kind === g.kind).map((f, i) => (
                    <motion.div
                      key={f.url}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group"
                    >
                      {f.mime_type?.startsWith('image/') ? (
                        <img src={f.url} alt={f.file_name} className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center ring-1 ring-slate-200">
                          <FileText className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((x) => x.url !== f.url))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="case-video" className="flex items-center gap-2">
              <Video className="w-4 h-4 text-slate-400" /> Link a un video (opzionale)
            </Label>
            <Input
              id="case-video"
              data-testid="case-video-input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://… (YouTube, Drive, WeTransfer)"
            />
          </div>
        </div>

        {formError && (
          <div
            className="mt-4 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
            data-testid="case-form-error"
          >
            {formError}
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            className="flex-1 bg-[#008080] hover:bg-[#006666] text-white font-semibold"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="case-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Invia richiesta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
